import {
  CIRCUIT_TYPE_TO_ICON,
  DEVICE_TYPE_TO_DEFAULT_APPLIANCE_ICON,
  MANUFACTURER_ID_TO_EV_CHARGER_ICON,
} from 'clipsal-cortex-icons/src/appliance-icon-maps';
import { OtherIcon } from 'clipsal-cortex-icons/src/custom-appliance-icons';
import { DeviceType } from 'clipsal-cortex-types/src/api';

import { useLiveData } from '../liveDataApi';

/**
 * Discerns whether certain paths in the animation should be rendered according to the property values in the live
 * data response.
 *
 * @returns An object containing boolean flag properties indicating whether each individual path should be rendered.
 */
export function useTilePathRenderOptions() {
  const { data: liveData, isLoading } = useLiveData({ shouldPoll: false });
  const shouldRenderGridPath = !isLoading && liveData.grid !== 0;
  const shouldRenderBatteryPath = !isLoading && !!liveData.battery && liveData.battery !== 0;
  const shouldRenderSolarBatteryPath = !isLoading && !!liveData.hybrid_inverter && liveData.hybrid_inverter !== 0;
  const shouldRenderSolarPath = !isLoading && liveData.hasOwnProperty('solar') && liveData.solar > 0;
  const shouldRenderHomePath = !isLoading && liveData.consumption !== 0;

  return {
    shouldRenderGridPath,
    shouldRenderBatteryPath,
    shouldRenderSolarBatteryPath,
    shouldRenderSolarPath,
    shouldRenderHomePath,
  };
}

/**
 * Returns the icon for a given appliance.
 * "Appliance" is here defined not strictly by its backend meaning but according to its current meaning on Cortex: "Some thing / measurement in the house that we represent as an individual icon."
 *
 * @param circuitType - The circuit type (if we have a CT measuring this thing).
 * @param deviceType - The type of the underlying device that we are representing.
 * @param manufacturerId - The manufacturer ID of the underlying device that we are representing.
 * @returns The appropriate icon.
 */
export function getApplianceIcon({
  circuitType,
  deviceType,
  manufacturerId,
}: {
  circuitType?: string;
  deviceType?: DeviceType;
  manufacturerId?: number;
}) {
  if (['EV_CAR', 'EV_CHARGER'].includes(deviceType ?? '')) {
    return manufacturerId != null
      ? MANUFACTURER_ID_TO_EV_CHARGER_ICON[manufacturerId.toString()]
      : CIRCUIT_TYPE_TO_ICON['load_ev_charger'];
  }
  if (!circuitType) {
    if (!deviceType) return OtherIcon;
    return DEVICE_TYPE_TO_DEFAULT_APPLIANCE_ICON[deviceType];
  }
  return CIRCUIT_TYPE_TO_ICON[circuitType] ?? CIRCUIT_TYPE_TO_ICON['load_other'];
}
